/**
 * Identifies the frequency mode of a communication channel (whether it is used to transmit, receive, or both). Note
 * that this mode pertains to the communication channel itself - what the pilot can do will be the reverse (e.g., if
 * the channel is only used to transmit, that means the pilot can only receive, and vice-versa).
 */
export var FrequencyMode;
(function (FrequencyMode) {
    /**
     * Communication channel may only be used to receive transmissions (pilot may transmit but not listen)
     */
    FrequencyMode[FrequencyMode["Receive"] = 0] = "Receive";
    /**
     * Communication channel is only used for transmitting (pilot may only listen, not transmit)
     */
    FrequencyMode[FrequencyMode["Transmit"] = 1] = "Transmit";
    /**
     * Communication channel is duplex (may be used to both transmit and listen)
     */
    FrequencyMode[FrequencyMode["Duplex"] = 2] = "Duplex";
    /**
     * Communication channel is unknown
     */
    FrequencyMode[FrequencyMode["Unknown"] = 3] = "Unknown";
})(FrequencyMode || (FrequencyMode = {}));
/**
 * Flight communication channel (e.g., CTAF, ASOS/AWOS, ATIS, flight service, etc.)
 */
var FlightComm = /** @class */ (function () {
    function FlightComm() {
    }
    Object.defineProperty(FlightComm.prototype, "formattedFreq", {
        /**
         * Gets the formatted frequency for display, which includes the T or R suffix for non-duplex channels.
         * @returns {string}
         */
        get: function () {
            if (!this.frequency) {
                return '';
            }
            switch (this.freqMode) {
                case FrequencyMode.Receive:
                    return this.frequency + "R";
                case FrequencyMode.Transmit:
                    return this.frequency + "T";
                default:
                    return this.frequency;
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Parses flight communication channel
     * @param data {any} flight communication channel as returned by API
     * @return {FlightComm} Parsed flight communication channel
     */
    FlightComm.parse = function (data) {
        return Object.assign(new FlightComm(), {
            flightCommId: data.flightCommId,
            airportId: data.airportId,
            service: data.service,
            radioCall: data.radioCall,
            freqMode: this.parseFrequencyMode(data.freqMode),
            frequency: data.frequency,
            sector: data.sector,
            phoneNumber: data.phoneNumber,
            frequencyId: data.frequencyId,
            shortName: data.shortName,
            phoneType: data.phoneType
        });
    };
    /**
     * Parses frequency mode
     * @param value {any} Frequency mode as returned by API
     * @return {FrequencyMode} Parsed frequency mode
     */
    FlightComm.parseFrequencyMode = function (value) {
        switch (value) {
            case 'Receive':
                return FrequencyMode.Receive;
            case 'Transmit':
                return FrequencyMode.Transmit;
            case 'Duplex':
                return FrequencyMode.Duplex;
            case 'Unknown':
                return FrequencyMode.Unknown;
            default:
                return null;
        }
    };
    /**
     * Formats frequency mode
     * @param {FrequencyMode} Frequency mode object
     * @return {string} Formatted frequency mode
     */
    FlightComm.formatFrequencyMode = function (value) {
        switch (value) {
            case FrequencyMode.Receive:
                return 'Receive';
            case FrequencyMode.Transmit:
                return 'Transmit';
            case FrequencyMode.Duplex:
                return 'Duplex';
            case FrequencyMode.Unknown:
                return 'Unknown';
        }
    };
    return FlightComm;
}());
export { FlightComm };
